import React from 'react';
import { getLanguage, t } from '../i18n';
import './ErrorMessage.css';

/**
 * ErrorMessage component for displaying errors to users
 * 
 * Requirements 8.3: Display user-friendly error messages for network errors
 * Requirements 10.4: Display API errors to user
 * 
 * @param {Object} props
 * @param {string|Error} props.error - Error message or Error object
 * @param {Function} props.onRetry - Optional callback for retry action
 * @param {Function} props.onDismiss - Optional callback to dismiss/clear error
 * @param {string} props.type - Error type: 'network', 'api', 'validation', or 'general' (default: 'general')
 */
function ErrorMessage({ error, onRetry, onDismiss, type = 'general' }) {
  // Handle null/undefined error
  if (!error) {
    return null;
  }

  // Extract error message
  let errorMessage = '';
  let isNetworkError = false;

  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    isNetworkError = error.isNetworkError || false;
  } else if (error.message) {
    errorMessage = error.message;
    isNetworkError = error.isNetworkError || false;
  } else {
    errorMessage = t('interface.common.unexpected');
  }

  // Determine error type if not explicitly provided
  if (type === 'general' && isNetworkError) {
    type = 'network';
  }

  // Translate infrastructure failures, not server validation details or user-authored content.
  if (getLanguage() === 'ko' && (isNetworkError || /unable to connect|failed to fetch|network (error|request failed)|connection failure/i.test(errorMessage))) {
    errorMessage = t('interface.common.networkError');
  }

  // Get icon based on error type
  const getIcon = () => {
    switch (type) {
      case 'network':
        return '🔌';
      case 'api':
        return '⚠️';
      case 'validation':
        return '❌';
      default:
        return '⚠️';
    }
  };

  // Get CSS class based on error type
  const getClassName = () => {
    return `app-error-notice app-error-notice--${type}`;
  };

  return (
    <div className={getClassName()} data-testid="error-message" role="alert">
      <div className="app-error-notice-content">
        <span className="app-error-notice-icon" aria-hidden="true">
          {getIcon()}
        </span>
        <span className="app-error-notice-text">{errorMessage}</span>
      </div>
      
      {(onRetry || onDismiss) && <div className="app-error-notice-actions">
        {onRetry && (
          <button 
            type="button"
            className="app-error-notice-btn app-error-notice-btn-retry"
            onClick={onRetry}
            aria-label={t('interface.common.retry')}
          >
            {t('interface.common.retry')}
          </button>
        )}
        
        {onDismiss && (
          <button 
            type="button"
            className="app-error-notice-btn app-error-notice-btn-dismiss"
            onClick={onDismiss}
            aria-label={t('interface.common.dismissError')}
          >
            {t('interface.common.dismiss')}
          </button>
        )}
      </div>}
    </div>
  );
}

export default ErrorMessage;
